import { useState } from "react"
import { ScrollArea } from "components/ui/scroll-area"

import type { EmailItemsReviewProps, ResendItemInfo } from "../types/components"
import { ItemType } from "../types/shared"
import {
  combineEmailItems,
  hasRejectedWaitingStatus,
  getResendableItems,
} from "../utils/item-helpers"
import { buildReviewData } from "../utils/review-helpers"

import { useItemSelection } from "../hooks/use-item-selection"
import { useReviewNotes } from "../hooks/use-review-notes"
import { useItemExpansion } from "../hooks/use-item-expansion"
import { useModals } from "../hooks/use-modals"

import { LoadingOverlay } from "./shared"
import { ReviewHeader, ReviewFooter, GlobalNoteCard, ReviewItemCard } from "./email-items-review"
import {
  FileViewerModal,
  IndividualEvidenceHistoryModal,
  RejectConfirmationModal,
  SpecialConfirmationModal,
  ResendConfirmationModal
} from "./modals"

export type { ItemReviewData } from "../types/components"

export const EmailItemsReview = ({
  email,
  onBack,
  onApproveItems,
  onResendEmail
}: EmailItemsReviewProps) => {
  const [showGlobalNote, setShowGlobalNote] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isResendLoading, setIsResendLoading] = useState(false)
  const [isResendConfirmationOpen, setIsResendConfirmationOpen] = useState(false)

  const allItems = combineEmailItems(email)
  const resendableItems = getResendableItems(allItems)

  const { selectedItems, toggleItem, toggleAll, isAllSelected, clearSelection } = useItemSelection(allItems)
  const { reviewNotes, globalNote, setGlobalNote, setItemNote, getItemNote } = useReviewNotes()
  const { toggleExpanded, isExpanded } = useItemExpansion()
  const {
    selectedFile,
    isFileViewerOpen,
    isRejectConfirmationOpen,
    isSpecialConfirmationOpen,
    selectedItemForHistory,
    auditHistoryData,
    isAuditHistoryLoading,
    openFileViewer,
    closeFileViewer,
    openRejectConfirmation,
    closeRejectConfirmation,
    openSpecialConfirmation,
    closeSpecialConfirmation,
    openHistoryModal,
    closeHistoryModal
  } = useModals()

  // Reenvio sempre envia todos os itens reenviáveis da solicitação
  // (definição do PO). A seleção de checkbox existe só pra Recusar.
  const getResendItemsInfo = (): ResendItemInfo[] =>
    resendableItems.map(item => ({
      name: (item.type === ItemType.DIVERGENCE ? item.name : item.number) || '-',
      type: item.type === ItemType.DIVERGENCE ? 'DIVERGENCE' : 'NSU'
    }))

  const handleRejectClick = () => {
    const hasSpecialStatus = hasRejectedWaitingStatus(selectedItems, allItems)
    hasSpecialStatus ? openSpecialConfirmation() : openRejectConfirmation()
  }

  const handleSubmitRejection = async () => {
    setIsLoading(true)
    try {
      const reviewData = buildReviewData(selectedItems, allItems, reviewNotes, globalNote)
      await onApproveItems(reviewData)
    } catch (error) {
      console.error('Erro ao reprovar itens:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleConfirmRejection = () => {
    handleSubmitRejection()
    closeRejectConfirmation()
  }

  const handleConfirmSpecialRejection = () => {
    handleSubmitRejection()
    closeSpecialConfirmation()
  }

  const handleResendClick = () => {
    setIsResendConfirmationOpen(true)
  }

  const handleConfirmResend = async () => {
    if (!onResendEmail) return

    setIsResendLoading(true)
    try {
      await onResendEmail(email.email_id)
      clearSelection()
    } catch (error) {
      console.error('Erro ao reenviar email:', error)
    } finally {
      setIsResendLoading(false)
      setIsResendConfirmationOpen(false)
    }
  }

  const isProcessing = isLoading || isResendLoading

  return (
    <div className="flex flex-col h-[calc(80vh-2rem)] relative">
      {isProcessing && (
        <LoadingOverlay
          message={isResendLoading ? "Reenviando email..." : "Processando reprovação..."}
        />
      )}

      <ReviewHeader
        email={email}
        selectedCount={selectedItems.size}
        isAllSelected={isAllSelected()}
        onBack={onBack}
        onToggleAll={toggleAll}
        isLoading={isProcessing}
      />

      <ScrollArea className="flex-1 overflow-hidden">
        <div className="space-y-3 p-4">
          {selectedItems.size > 0 && (
            <GlobalNoteCard
              selectedCount={selectedItems.size}
              isExpanded={showGlobalNote}
              note={globalNote}
              onToggle={() => setShowGlobalNote(!showGlobalNote)}
              onNoteChange={setGlobalNote}
            />
          )}

          <div className="space-y-2">
            {allItems.map((item) => (
              <ReviewItemCard
                key={item.id}
                item={item}
                isSelected={selectedItems.has(item.id)}
                isExpanded={isExpanded(item.id)}
                reviewNote={getItemNote(item.id)}
                onSelect={() => toggleItem(item.id)}
                onToggleExpanded={() => toggleExpanded(item.id)}
                onNoteChange={(value) => setItemNote(item.id, value)}
                onFileClick={openFileViewer}
                onShowHistory={() => openHistoryModal(item)}
                isLoading={isProcessing}
              />
            ))}
          </div>
        </div>
      </ScrollArea>

      <ReviewFooter
        selectedCount={selectedItems.size}
        resendableCount={resendableItems.length}
        hasResendableItems={resendableItems.length > 0}
        onBack={onBack}
        onReject={handleRejectClick}
        onResend={onResendEmail ? handleResendClick : undefined}
        isLoading={isProcessing}
      />

      <FileViewerModal
        file={selectedFile}
        isOpen={isFileViewerOpen}
        onClose={closeFileViewer}
      />

      <RejectConfirmationModal
        isOpen={isRejectConfirmationOpen}
        onClose={closeRejectConfirmation}
        onConfirm={handleConfirmRejection}
        selectedCount={selectedItems.size}
        isLoading={isLoading}
      />

      <SpecialConfirmationModal
        isOpen={isSpecialConfirmationOpen}
        onClose={closeSpecialConfirmation}
        onConfirm={handleConfirmSpecialRejection}
        selectedCount={selectedItems.size}
        isLoading={isLoading}
      />

      <ResendConfirmationModal
        isOpen={isResendConfirmationOpen}
        onClose={() => setIsResendConfirmationOpen(false)}
        onConfirm={handleConfirmResend}
        selectedCount={resendableItems.length}
        isLoading={isResendLoading}
        items={getResendItemsInfo()}
      />

      {selectedItemForHistory && (
        <IndividualEvidenceHistoryModal
          isOpen={!!selectedItemForHistory}
          onClose={closeHistoryModal}
          item={selectedItemForHistory}
          auditData={auditHistoryData}
          isAuditLoading={isAuditHistoryLoading}
        />
      )}
    </div>
  )
}
